import FeeStructureEditLogs from "./FeeStructureEditLogs";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { saveAs } from "file-saver";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import IconButton from "../../DesignSystem/Buttons/IconButton";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import RadioGroup from "../../DesignSystem/Inputs/RadioGroup";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as DropdownIcon } from "../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { subscribeAdvisersData } from "../../../context/Adviser/Utils/ExportAdvisersData";
import { APP_ROUTES } from "../../../constants/appRoutes";
import {
  CATEGORIES,
  adviserCategories,
  canManageFees,
  isAppointedFeeAdviser,
  sortVersions,
  visibleCategories,
} from "./feeStructureModel";
import {
  saveFeeStructure,
  subscribeFeeStructures,
} from "./feeStructureService";
import FeeStructureForm from "./FeeStructureForm";
import FeeStructureVersionsTable from "./FeeStructureVersionsTable";
import { money, TierTable, refundableLabel } from "./FeeStructureDetails";
import {
  PostCard,
} from "../../Adviser/Tabs/HelperFunctions/HelperFunctions";
import AdviserDetailsCard from "../../Adviser/Tabs/Rates/AdviserDetailsCard";
import "../../../css/Adviser/AdviserDetailsPage.css";
import "../../../css/Adviser/EditAdviserDetailsPage.css";
import "../../../css/Adviser/LicenseCertificatesSummary.css";
import "../../../css/Compliance/ClientAgreementFeeStructures/ClientAgreementFeeStructures.css";

const base = APP_ROUTES.CLIENT_AGREEMENT_FEE_STRUCTURES;
const adviserName = (a) =>
  [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email;
const practice = (a) => a.tradingName || a.tradingStyle || a.brandName || "-";
const editUrl = (id, category) =>
  `/advisers/${encodeURIComponent(id)}/client-agreement/fees/${category}/edit`;

function FeeSummaryCells({ value, category }) {
  return (
    <>
      <td>
        {value
          ? `${money(value.fixedFee)} / ${refundableLabel(value.refundable)}`
          : "-"}
      </td>
      {category === "financial-planning" ? (
        <>
          <td>
            <TierTable rows={value?.tiers} showHeader={false} />
          </td>
          <td>
            {value?.ongoingType === "tiered" ? (
              <TierTable rows={value.ongoingTiers} showHeader={false} />
            ) : value?.ongoingRate !== "" && value?.ongoingRate != null ? (
              <TierTable rows={[{ rate: value.ongoingRate }]} showHeader={false} rateOnly />
            ) : (
              "-"
            )}
          </td>
        </>
      ) : (
        <td>{value?.variations || "-"}</td>
      )}
    </>
  );
}

export default function ClientAgreementFeeStructuresPage({ edit = false }) {
  const { adviserKey, category: routeCategory } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const returnTo =
    state?.returnTo ||
    `/advisers/${encodeURIComponent(adviserKey || "")}?tab=clientAgreement`;
  const { userPermissions = [] } = useUserPermissions();
  const canAccess = canManageFees(userPermissions);
  const [advisers, setAdvisers] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [sort, setSort] = useState({ key: "practice", direction: 1 });
  const [historyOpen, setHistoryOpen] = useState({});
  const [editTab, setEditTab] = useState("details");

  const feeFormRef = useRef(null);
  const [savingVersion, setSavingVersion] = useState(false);
  const [categoryTab, setCategoryTab] = useState("financial-planning");
  const [success, setSuccess] = useState("");
  useEffect(() => {
    if (!canAccess) return;
    return subscribeAdvisersData(
      setAdvisers,
      () => {
        setError("Could not load advisers.");
        setLoading(false);
      },
      { waitForPersonalDetails: true },
    );
  }, [canAccess]);
  const adviserIds = JSON.stringify(advisers.map((a) => a.id).sort());
  useEffect(() => {
    if (!canAccess) return;
    setLoading(true);
    return subscribeFeeStructures(
      JSON.parse(adviserIds),
      (data) => {
        setVersions(data);
        setLoading(false);
      },
      () => {
        setError("Could not load fee structures. Please try again.");
        setLoading(false);
      },
    );
  }, [adviserIds, canAccess]);
  const tabs = visibleCategories(userPermissions, advisers);
  const activeCategoryTab = tabs.some(c => c.id === categoryTab) ? categoryTab : tabs[0]?.id;
  const getVersions = (id, cat) =>
    sortVersions(
      versions.filter((v) => v.adviserId === id && v.category === cat),
    );
  if (!canAccess) return <Navigate to="/Department/Compliance" replace />;
  const adviser = advisers.find((a) => a.id === adviserKey);
  const relevantTabs = adviser
    ? CATEGORIES.filter((c) => adviserCategories(adviser, versions.filter(v => v.adviserId === adviser.id).map(v => v.category)).includes(c.id))
    : [];
  const editAllowed =
    adviser && relevantTabs.some((c) => c.id === routeCategory);
  const currentVersions = adviser ? getVersions(adviser.id, routeCategory) : [];
  const current = currentVersions[0];
  const filterAdvisers = (activeCategory) =>
    advisers
      .filter(
        (a) =>
          isAppointedFeeAdviser(a) &&
          adviserCategories(a, versions.filter(v => v.adviserId === a.id).map(v => v.category)).includes(activeCategory) &&
          `${practice(a)} ${adviserName(a)} ${a.email}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      )
      .sort(
        (a, b) =>
          (sort.key === "practice"
            ? practice(a).localeCompare(practice(b))
            : adviserName(a).localeCompare(adviserName(b))) * sort.direction,
      );
  const sortBy = (key) =>
    setSort((s) => ({ key, direction: s.key === key ? -s.direction : 1 }));
  const downloadExcel = async () => {
    if (loading || downloading || !activeCategoryTab) return;
    setDownloading(true); setError("");
    try {
      const { exportFeeStructuresExcel } = await import("./exportFeeStructuresExcel");
      const result = await exportFeeStructuresExcel({ sheets: tabs.map(tab => ({ advisers: filterAdvisers(tab.id), versions, category: tab.id, expanded: historyOpen })) });
      saveAs(result.blob, result.filename);
    } catch {
      setError("Could not download the fee structures. Please try again.");
    } finally { setDownloading(false); }
  };
  return (
    <div className="main-content fee-page-shell">
      <Helmet>
        <title>{edit ? "Edit " : ""}Client Agreement Fee Structures</title>
      </Helmet>
      <Sidebar isSetExpanded={!edit} />
      <div className={`Main-Container fee-page fit-column gap-m${edit ? "" : " fee-page--summary"}`}>
        <Breadcrumb
          items={edit ? [
            { label: "Advisers", to: "/Advisers" },
            { label: adviser ? adviserName(adviser) : adviserKey, to: `/advisers/${encodeURIComponent(adviserKey || "")}` },
            { label: "Client Agreement", to: `/advisers/${encodeURIComponent(adviserKey || "")}?tab=clientAgreement` },
            { label: "Edit Fee Structure" },
          ] : [
            { label: "Compliance", to: "/Department/Compliance" },
            { label: "Client Agreement Fee Structures" },
          ]}
        />
        {!edit && <div className="fee-page-heading">
          <h3 className="FP_H3">
            {edit ? "Edit " : ""}Client Agreement Fee Structures
          </h3>
          {!edit && (
            <div className="inline-flex align-center gap-s"><div className="fee-search">
              <SearchField
                id="fee-structure-search"
                label=""
                placeholder="Adviser or practice"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div><IconButton icon={<DownloadIcon />} label="Download Excel" loading={downloading} loadingLabel="Preparing Excel..." disabled={loading || !tabs.some(tab => filterAdvisers(tab.id).length)} onClick={downloadExcel} /></div>
          )}
        </div>}
        {error && (
          <p role="alert" className="text-error">
            {error}
          </p>
        )}
        {success && <p role="status">{success}</p>}
        {loading ? (
          <p role="status">Loading fee structures...</p>
        ) : edit ? (
          !editAllowed ? (
            <p role="alert">
              This adviser does not have permission for this fee category, or
              could not be found.
            </p>
          ) : (
            <>
              <div className="smEditActions">
                <h3 className="FP_H3">{adviserName(adviser)}</h3>
                <div className="row gap-s align-center">
                <PrimaryButton
                  type="button"
                  label={savingVersion ? "Saving..." : "Save Version"}
                  disabled={savingVersion || editTab !== "details"}
                  onClick={() => feeFormRef.current?.requestSubmit()}
                />
                <SecondaryButton label="Cancel" disabled={savingVersion} onClick={() => navigate(returnTo)} />
                </div>
              </div>
              <div className="smRatesEditLayout">
                <div className="fit-column gap-m">
                  <RadioGroup
                    name="edit-fee-category"
                    inline
                    options={relevantTabs.map((c) => ({
                      value: c.id,
                      label: c.label,
                    }))}
                    value={routeCategory}
                    onChange={(e) => {
                      setEditTab("details");
                      navigate(editUrl(adviser.id, e.target.value));
                    }}
                  />
                  <Tabs
                    tabs={[
                      { id: "details", label: "Fee Details" },
                      { id: "history", label: "Versions" },
                    ]}
                    activeTab={editTab}
                    onTabChange={setEditTab}
                  />
                  {editTab === "history" ? (
                    <PostCard title="Versions">
                      <FeeStructureVersionsTable versions={currentVersions} category={routeCategory} />
                    </PostCard>
                  ) : (
                    <PostCard title="Edit Fee Structure">
                      <FeeStructureForm
                        formRef={feeFormRef}
                        hideActions
                        onSavingChange={setSavingVersion}
                        key={`${adviser.id}-${routeCategory}`}
                        initial={current}
                        category={routeCategory}
                        previousVersion={current?.version}
                        onCancel={() => navigate(returnTo)}
                        onSave={async (form, sourceId) => {
                          await saveFeeStructure(
                            adviser.id,
                            routeCategory,
                            form,
                            sourceId,
                          );
                          setSuccess(
                            "New fee structure version saved. Previous versions remain available.",
                          );
                          navigate(returnTo);
                        }}
                      />
                    </PostCard>
                  )}
                  <FeeStructureEditLogs versions={currentVersions} />
                </div>
                <AdviserDetailsCard adviser={adviser} application={adviser} />
              </div>
            </>
          )
        ) : (
          <>
            <Tabs tabs={tabs} activeTab={activeCategoryTab || ""} onTabChange={setCategoryTab} />
            {tabs.filter(c => c.id === activeCategoryTab).map((c) => {
              const activeCategory = c.id;
              const filtered = filterAdvisers(activeCategory);
              return (
                <section key={c.id} className="fee-category-table">
                  {/* <h4 className="FP_Body_Semibold">{c.label}</h4> */}
                  <div className="fee-table-scroll">
                    <table className="fee-table">
                      <thead>
                        <tr>
                          <th
                            className="FP_Help_Text_Semibold"
                            aria-sort={
                              sort.key === "practice"
                                ? sort.direction === 1
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <button
                              type="button"
                              className="table-heading-content table-sort-button"
                              onClick={() => sortBy("practice")}
                            >
                              Practice <SortIcon aria-hidden />
                            </button>
                          </th>
                          <th
                            className="FP_Help_Text_Semibold"
                            aria-sort={
                              sort.key === "adviser"
                                ? sort.direction === 1
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <button
                              type="button"
                              className="table-heading-content table-sort-button"
                              onClick={() => sortBy("adviser")}
                            >
                              Adviser <SortIcon aria-hidden />
                            </button>
                          </th>
                          <th className="FP_Help_Text_Semibold">
                            Client Agreement Version
                          </th>
                          <th className="FP_Help_Text_Semibold">
                            Fixed Fee (£) / Refundable
                          </th>
                          {activeCategory === "financial-planning" ? (
                            <>
                              <th className="FP_Help_Text_Semibold">
                                Progressive Tiered Fee Structure
                              </th>
                              <th className="FP_Help_Text_Semibold">
                                Ongoing Fee (%)
                              </th>
                            </>
                          ) : (
                            <th className="FP_Help_Text_Semibold">
                              Fee With Variation
                            </th>
                          )}
                          <th>
                            <span className="fee-sr-only">Actions</span>
                          </th>
                        </tr>
                        {activeCategory === "financial-planning" && <tr className="fee-rate-headings">
                          {Array.from({ length: 4 }, (_, index) => <th key={index} aria-hidden="true" />)}
                          {["Progressive tiers", "Ongoing tiers"].map(label => <th key={label}>
                            <div className="fee-tier-column-headings" aria-label={label}><span className="FP_Help_Text_Semibold">{label === "Ongoing tiers" ? "Rate (% p.a.)" : "Rate (%)"}</span><span className="FP_Help_Text_Semibold">From</span><span className="FP_Help_Text_Semibold">To</span></div>
                          </th>)}
                          <th aria-hidden="true" />
                        </tr>}
                      </thead>
                      <tbody>
                        {filtered.map((a, adviserIndex) => {
                          const rowStyle = { "--fee-row-background": adviserIndex % 2 ? "var(--Blue200)" : "var(--White)" };
                          const all = getVersions(a.id, activeCategory);
                          const latest = all[0];
                          const rowKey = `${a.id}-${activeCategory}`;
                          const expanded = !!historyOpen[rowKey];
                          const toggle = () =>
                            setHistoryOpen((s) => ({
                              ...s,
                              [rowKey]: !s[rowKey],
                            }));
                          return (
                            <React.Fragment key={a.id}>
                              <tr className={`fee-adviser-row${expanded ? " fee-group-continued" : ""}`} style={rowStyle}>
                                <td>{practice(a)}</td>
                                <td>
                                  <Link
                                    className="lcClickableTextCell FP_Help_Text primary-text"
                                    state={{ returnTo: base }}
                                    to={editUrl(a.id, activeCategory)}
                                  >
                                    {adviserName(a)}
                                  </Link>
                                </td>
                                <td>
                                  {latest?.version || "-"}
                                </td>
                                <FeeSummaryCells
                                  value={latest}
                                  category={activeCategory}
                                />
                                <td>
                                  <button
                                    type="button"
                                    className="fee-expand-arrow"
                                    aria-expanded={expanded}
                                    aria-label={`${expanded ? "Collapse" : "Expand"} fee structure for ${adviserName(a)}`}
                                    onClick={toggle}
                                  >
                                    <DropdownIcon
                                      aria-hidden
                                      width={16}
                                      height={16}
                                      style={{
                                        transform: expanded
                                          ? "rotate(180deg)"
                                          : undefined,
                                      }}
                                    />
                                  </button>
                                </td>
                              </tr>
                              {expanded &&
                                all.slice(1).map((v, versionIndex) => (
                                  <tr className={`fee-adviser-row fee-previous-row${versionIndex < all.length - 2 ? " fee-group-continued" : ""}`} style={rowStyle} key={v.id}>
                                    <td />
                                    <td />
                                    <td>
                                      {v.version}
                                      <div className="Content-Small muted">
                                        {v.date}
                                      </div>
                                    </td>
                                    <FeeSummaryCells
                                      value={v}
                                      category={activeCategory}
                                    />
                                    <td />
                                  </tr>
                                ))}
                              {expanded && all.length < 2 && (
                                <tr className="fee-adviser-row fee-expanded-footer" style={rowStyle}>
                                  <td
                                    colSpan={
                                      activeCategory === "financial-planning"
                                        ? 7
                                        : 6
                                    }
                                  >
                                    No other fees.
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {!filtered.length && (
                          <tr>
                            <td
                              colSpan={
                                activeCategory === "financial-planning" ? 7 : 6
                              }
                            >
                              No advisers with the relevant business permissions
                              match your search.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
